import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Create,
  Form,
  Icons,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Upload,
  getValueFromEvent,
  notification,
  useForm,
} from '@pankod/refine-antd';
import React from 'react';
import { file2Base64, useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import avatarPlaceholder from 'assets/images/avatar-placeholder.png';
import { CustomSelect, TemplatesForm } from 'components';
import { ICategory, ITag } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllCategoryDropdown } from 'services/category';
import { IProductNew, getProductNew } from 'services/product';
import { createVariant } from 'services/variant';
import { getAllTagDropdown } from 'services/tag';
import { ITemplate, getTemplatesOfProduct } from 'services/template';
import {
  beforeImageUploadValidation,
  isJpgOrPngFile,
  isLessThanSize,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import { getRandomString } from 'utilities/shared';

const { Option } = Select;

export const VariantCreate = () => {
  const [product, setProduct] = useState<IProductNew>();
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [tags, setTags] = useState<ITag[]>([]);
  const [isProductLoading, setIsProductLoading] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const { form, formProps, saveButtonProps } = useForm();
  const { id: productId } = useParams();
  const { show } = useNavigation();

  useEffect(() => {
    if (!productId) return;

    (async () => {
      setIsProductLoading(true);
      try {
        const [productResponse, productTemplatesResponse, categoryResponse, tagResponse] =
          await Promise.all([
            getProductNew(productId),
            getTemplatesOfProduct(productId),
            getAllCategoryDropdown(),
            getAllTagDropdown(),
          ]);

        const selectedCategory = productResponse.productCategory?.id || null;
        const selectedTags = productResponse.productTags.map((item) => item.tag.name);
        const randomName = getRandomString(4, 'A');
        const randomSku = getRandomString(4, 'A#');
        form.setFieldsValue({
          name: `${productResponse.name} ${randomName}`,
          sku: `${productResponse.sku}-${randomSku}`,
          purchasePrice: productResponse.purchasePrice,
          sellingPrice: productResponse.sellingPrice,
          category: selectedCategory,
          tags: selectedTags,
        });

        setProduct(productResponse);
        setTemplates(productTemplatesResponse);
        setCategories(categoryResponse);
        setTags(tagResponse);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsProductLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const getOptions = (items: ICategory[] | ITag[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const pageTitle = product ? `Create ${product?.name} variant` : 'Create variant';
  const customFields = product?.customFields ?? [];

  const onFinish = async (values: any) => {
    if (!productId || !product) return;

    const { avatar } = values;
    const base64Files = [];

    if (avatar && avatar.length) {
      const file = avatar[0];
      const format = file.type || '';
      const size = file.size || 0;

      if (
        isJpgOrPngFile(format, file.name || '') &&
        isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)
      ) {
        if (file.originFileObj) {
          const base64String = await file2Base64(file);
          const { originFileObj, ...rest } = file;
          base64Files.push({ ...rest, base64String });
        } else {
          base64Files.push(file);
        }
      }
    }

    const variantAttributes = Object.values(values.templates ?? [])
      .flat()
      .map((item: any) => {
        const value = item?.value ?? null;
        return {
          attributeId: item.attributeId,
          value: value === null ? null : !value.toString().trim() ? null : value.toString().trim(),
        };
      });

    const customFields = values.customFields ?? [];
    const customFieldValues: { key: string; value: string }[] = [];
    for (const [key, value] of Object.entries(customFields)) {
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        continue;
      }
      customFieldValues.push({ key, value: value.toString() as string });
    }

    const data = {
      productId: product.id,
      name: values.name,
      sku: values.sku,
      description: values.description,
      purchasePrice: values.purchasePrice,
      sellingPrice: values.sellingPrice,
      productCategory: values.category,
      productTags: values.tags ?? [],
      productWarehouses: [],
      photo: base64Files[0],
      templateIds: templates.map((template) => template.id),
      customFields: customFieldValues,
      variantAttributes,
    };

    setIsCreating(true);
    try {
      await createVariant(data);
      notification.success({
        message: 'Success',
        description: 'Successfully created.',
      });
      show('products', productId);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsCreating(false);
  };

  return (
    <Create
      title={pageTitle}
      isLoading={isProductLoading}
      saveButtonProps={{ ...saveButtonProps, disabled: isCreating }}
    >
      <Form
        {...formProps}
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          isActive: true,
        }}
      >
        <Row gutter={[56, 0]} wrap>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              style={{
                width: '100%',
                textAlign: 'center',
                marginBottom: '16px',
                marginTop: '6px',
              }}
            >
              <Form.Item
                name="avatar"
                valuePropName="fileList"
                getValueFromEvent={getValueFromEvent}
                noStyle
              >
                <Upload.Dragger
                  listType="picture"
                  accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
                  maxCount={1}
                  beforeUpload={beforeImageUploadValidation}
                >
                  <Avatar
                    size={200}
                    style={{ objectFit: 'cover' }}
                    src={avatarPlaceholder}
                    alt="Store Location"
                  />
                  <p className="ant-upload-text">Drag & drop an image file in this area</p>
                </Upload.Dragger>
              </Form.Item>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label="Product Name"
              name="name"
              rules={[
                {
                  required: true,
                },
                {
                  pattern: /^[^,%'`";*^!*.=~:?/\\<>]+$/,
                  message: 'Only alphabets, numbers, -_@#$)( are acceptable',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="SKU"
              name="sku"
              rules={[
                {
                  required: true,
                },
                {
                  pattern: /^[^,%'`";*^!*.=~?()/\\<>]+$/,
                  message: 'Only alphabets, numbers, -_@#$ are acceptable',
                },
              ]}
            >
              <Input
                suffix={
                  <Button
                    size="small"
                    icon={<Icons.SyncOutlined />}
                    onClick={() => form.setFieldsValue({ sku: getRandomString(8, 'aA#') })}
                  />
                }
              />
            </Form.Item>
            <Form.Item label="Category" name="category">
              <Select
                style={{ width: '100%' }}
                placeholder="Select Category"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {getOptions(categories)}
              </Select>
            </Form.Item>
            <Form.Item label="Tags" name="tags">
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Select tags"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {tags.map((tag) => (
                  <Option key={tag.name} value={tag.name}>
                    {tag.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Product Details" name="description">
              <Input.TextArea rows={8} />
            </Form.Item>

            <Row gutter={[56, 0]}>
              <Col lg={12} md={12} sm={24}>
                <Form.Item
                  label="Suggested Purchase Price"
                  name="purchasePrice"
                  rules={[
                    { required: true },
                    { type: 'number', min: 0, message: 'Purchase price cannot be less than 0' },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} precision={2} />
                </Form.Item>
              </Col>
              <Col lg={12} md={12} sm={24}>
                <Form.Item
                  label="Suggested Selling Price"
                  name="sellingPrice"
                  rules={[
                    { required: true },
                    { type: 'number', min: 0, message: 'Selling price cannot be less than 0' },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} precision={2} />
                </Form.Item>
              </Col>
            </Row>

            <TemplatesForm name="templates" templates={templates} />

            {customFields.length > 0 ? (
              <Card title="Custom Fields" style={{ marginTop: '15px' }}>
                <Form.Item name="customFields">
                  {customFields.map((attribute) => {
                    return (
                      <React.Fragment key={attribute.name}>
                        <Form.Item
                          label={attribute.name.toUpperCase()}
                          name={['customFields', attribute.name]}
                          {...(attribute.type === 'boolean' && { valuePropName: 'checked' })}
                          initialValue={(() => {
                            if (attribute.type === 'boolean') {
                              return false;
                            } else if (attribute.type === 'dropdown') {
                              return attribute.options[0];
                            } else {
                              return null;
                            }
                          })()}
                          rules={[
                            {
                              required: attribute.required,
                              message: `${attribute.name} is required`,
                            },
                          ]}
                        >
                          {(() => {
                            if (attribute.type === 'float') {
                              return <InputNumber style={{ width: '100%' }} />;
                            } else if (attribute.type === 'boolean') {
                              return <Checkbox>Yes / No</Checkbox>;
                            } else if (attribute.type === 'dropdown') {
                              return (
                                <CustomSelect
                                  style={{ width: '100%' }}
                                  defaultValue={attribute.options[0]}
                                  placeholder={`select ${attribute.name}`}
                                  filterOption={true}
                                  allowCustom={attribute.allowCustomValue}
                                  items={attribute.options}
                                />
                              );
                            } else {
                              return <Input />;
                            }
                          })()}
                        </Form.Item>
                      </React.Fragment>
                    );
                  })}
                </Form.Item>
              </Card>
            ) : null}
          </Col>
        </Row>
      </Form>
    </Create>
  );
};
